import { Typography } from "@circle-react-uikit/Typography";
import type { ShowResponseEndpoint } from "@circle-react/api/paywallCheckoutApi.types";
import { usePaywallCheckoutContext } from "@circle-react/contexts/Paywalls/paywallCheckoutContext";
import { formatPaywallCurrencyAmount } from "@circle-react/helpers/paywalls/currencies";
import type { Paywall } from "@circle-react/types/Paywall";
import { Icon } from "@circleco/compass/components/Icon";
import { IconButton } from "@circleco/compass/components/IconButton";
import { Typography as CompassTypography } from "@circleco/compass/components/Typography";
import { useFormContext } from "react-hook-form";

import { t } from "@/i18n-js/instance";
import { TippyV2 } from "@/react/components/SettingsApp/CompassCompat/Tooltip";

import {
  CouponApplicationStatus,
  useGetCouponApplication,
} from "../../../hooks/useGetCouponApplication";

const localeRoot = "paywall_checkout.coupon_badge";

const presentCoupon = (
  paywallCoupon: NonNullable<ShowResponseEndpoint["paywall_coupon"]>,
  currency: NonNullable<Paywall["currency"]>,
): string =>
  t([localeRoot, paywallCoupon.duration], {
    amount:
      paywallCoupon.type === "PaywallCoupons::Percent"
        ? `${Number(paywallCoupon?.amount)}%`
        : formatPaywallCurrencyAmount(currency, Number(paywallCoupon?.amount), {
            amountInCents: false,
            includeCurrencyCode: false,
          }),
    count: paywallCoupon?.duration_in_months,
  });

export const CouponBadge = () => {
  const { watch } = useFormContext();
  const { couponPreviewMutation, clearCouponCode, getSelectedPrice } =
    usePaywallCheckoutContext();

  const couponCode = watch("coupon_code");
  const onRemoveCoupon = () => {
    clearCouponCode({ resetPreview: true });
  };
  const couponData = couponPreviewMutation?.data?.paywall_coupon;
  const currencyData = couponPreviewMutation?.data?.paywall?.currency;

  const paywallPriceId = getSelectedPrice()?.id;
  const couponApplicationStatus = useGetCouponApplication(
    paywallPriceId,
    couponPreviewMutation,
  );

  if (couponPreviewMutation.isPending) {
    return null;
  }

  return (
    <>
      <div
        className="bg-secondary flex h-9 w-fit max-w-full flex-row items-center justify-between gap-x-1 overflow-hidden rounded-md px-3 sm:w-fit md:gap-x-3"
        data-testid="checkout-form-coupon-code"
      >
        <Icon name="tag" size="sm" color="primary" className="shrink-0" />
        <div className="flex min-w-0 max-w-[80%] shrink flex-row items-center gap-x-2">
          <Typography.BodySm weight="medium" color="text-dark" truncate>
            {couponCode}
          </Typography.BodySm>

          {couponData && currencyData && (
            <TippyV2
              content={presentCoupon(couponData, currencyData)}
              placement="top"
              className="text-default truncate text-xs font-normal normal-case leading-4 tracking-normal"
            >
              {presentCoupon(couponData, currencyData)}
            </TippyV2>
          )}
        </div>

        <IconButton
          icon="cross"
          size="sm"
          variant="ghost"
          onClick={onRemoveCoupon}
          aria-label={t("paywall_checkout.form.buttons.remove_coupon")}
          data-testid="checkout-form-remove-coupon"
        />
      </div>
      {couponApplicationStatus.status !==
        CouponApplicationStatus.COUPON_APPLIED && (
        <CompassTypography variant="body-sm" color="danger" role="alert">
          {t(
            "paywall_checkout.form.errors.paywall_coupon_does_not_apply_to_price",
          )}
        </CompassTypography>
      )}
    </>
  );
};
